import React from 'react';
import PropTypes from 'prop-types';
import { Select } from '../Select/Select';
import './FormInput.scss';

/**
 * FormInput Component
 *
 * A reusable form input component supporting text, select, textarea, checkbox, and number inputs.
 * Designed for modal forms with glassmorphism styling.
 * Uses the custom Select component for select inputs with search and dropdown functionality.
 *
 * Features:
 * - Multiple input types (text, select, textarea, checkbox, number)
 * - Required field validation
 * - Placeholder support
 * - Options for select dropdowns (auto-converts string[] to Select format)
 * - Full width or grid layout support
 * - Glassmorphism styling
 * - Accessible with proper labels and IDs
 * - Select inputs use custom Select component with search
 * - Autocomplete disabled by default (can be overridden via inputProps)
 *
 * @param {Object} props
 * @param {string} props.id - Input ID (required for accessibility)
 * @param {string} props.label - Input label text
 * @param {string} props.type - Input type: 'text', 'select', 'textarea', 'checkbox', 'number' (default: 'text')
 * @param {string|boolean|number|array} props.value - Input value (array for multiselect)
 * @param {Function} props.onChange - Change handler (value) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Whether field is required (default: false)
 * @param {Array} props.options - Options for select: [{value, label}] or string array
 * @param {number} props.rows - Rows for textarea (default: 3)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.checked - Checked state for checkbox
 * @param {Object} props.inputProps - Additional props passed to input element
 * @param {string} props.error - Error message to display below the input
 * @param {Function} props.onBlur - Blur handler for validation
 * @param {boolean} props.showErrorMsg - Whether to display error message (default: true)
 * @param {boolean} props.showSearch - Whether to show search in select dropdown (default: true)
 * @param {boolean} props.disabled - Whether the input is disabled (default: false)
 *
 * @example
 * // Text input
 * <FormInput
 *   id="title"
 *   label="Title"
 *   value={title}
 *   onChange={(value) => setTitle(value)}
 *   placeholder="Enter title"
 *   required
 * />
 *
 * @example
 * // Select dropdown
 * <FormInput
 *   id="country"
 *   label="Country"
 *   type="select"
 *   value={country}
 *   onChange={(value) => setCountry(value)}
 *   options={['USA', 'UK', 'Canada']}
 *   required
 * />
 *
 * @example
 * // Textarea
 * <FormInput
 *   id="description"
 *   label="Description"
 *   type="textarea"
 *   value={description}
 *   onChange={(value) => setDescription(value)}
 *   rows={5}
 * />
 *
 * @example
 * // Checkbox
 * <FormInput
 *   id="currentlyWorking"
 *   label="I'm currently working here"
 *   type="checkbox"
 *   checked={isWorking}
 *   onChange={(checked) => setIsWorking(checked)}
 * />
 */
export const FormInput = ({
  id,
  label,
  type = 'text',
  value = '',
  onChange,
  onBlur,
  placeholder = '',
  required = false,
  options = [],
  rows = 3,
  className = '',
  focusVariant = '',
  checked = false,
  inputProps = {},
  error = '',
  showErrorMsg = true,
  disabled = false,
  showSearch, // Extract showSearch to prevent it from being passed to DOM
  ...props
}) => {
  // Handle change for different input types
  const handleChange = (e) => {
    if (type === 'checkbox') {
      onChange(e.target.checked);
    } else {
      onChange(e.target.value);
    }
  };

  // Render select dropdown using Select component
  const renderSelect = () => {
    // Convert string array to object array format required by Select component
    // Ensure all options have both value and label properties
    const formattedOptions = options.map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      // Ensure object options have label property
      return {
        value: option.value || option,
        label: option.label || option.value || String(option),
      };
    });

    return (
      <Select
        options={formattedOptions}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder || 'Select an option'}
        error={!!error}
        disabled={disabled}
        focusVariant={focusVariant}
        {...inputProps}
        {...props}
      />
    );
  };

  // Render textarea
  const renderTextarea = () => (
    <textarea
      id={id}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      rows={rows}
      autoComplete='off'
      disabled={disabled}
      className={`${error ? 'has-error' : ''}`.trim()}
      {...inputProps}
    />
  );

  // Render checkbox
  const renderCheckbox = () => (
    <div className='form-input-checkbox-wrapper'>
      <input
        type='checkbox'
        id={id}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        {...inputProps}
        {...props}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );

  // Render text input (including number type)
  const renderTextInput = () => (
    <input
      id={id}
      type={type === 'number' ? 'number' : type}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      autoComplete='off'
      disabled={disabled}
      className={`${error ? 'has-error' : ''}`.trim()}
      {...inputProps}
      {...props}
    />
  );

  // Checkbox has different layout
  if (type === 'checkbox') {
    return (
      <div
        className={`form-input form-input-checkbox form-input--focus-${focusVariant} ${className}`}
      >
        {renderCheckbox()}
      </div>
    );
  }

  // Standard layout for text, select, textarea
  return (
    <div
      className={`form-input form-input--focus-${focusVariant} ${
        error ? 'has-error' : ''
      } ${className}`}
    >
      {label && <label htmlFor={id}>{label}</label>}
      {type === 'select' && renderSelect()}
      {type === 'textarea' && renderTextarea()}
      {type !== 'select' &&
        type !== 'textarea' &&
        type !== 'checkbox' &&
        renderTextInput()}
      {error && showErrorMsg && (
        <span className='form-input-error'>{error}</span>
      )}
    </div>
  );
};

FormInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'select', 'textarea', 'checkbox', 'number']),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array, // For multiselect values
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  options: PropTypes.array,
  rows: PropTypes.number,
  className: PropTypes.string,
  focusVariant: PropTypes.string,
  checked: PropTypes.bool,
  inputProps: PropTypes.object,
  error: PropTypes.string,
  showErrorMsg: PropTypes.bool,
  showSearch: PropTypes.bool,
  disabled: PropTypes.bool,
};

FormInput.defaultProps = {
  type: 'text',
  value: '', // Can be string, number, boolean, or array
  placeholder: '',
  required: false,
  options: [],
  rows: 3,
  className: '',
  focusVariant: '',
  checked: false,
  inputProps: {},
  error: '',
  showErrorMsg: true,
  showSearch: true,
  disabled: false,
};
